import { z } from 'zod';

import { ActiveElement, ArchivedElement, Tombstone } from '../../heymerge';
import objectPassthrough from '../../objectPassthrough';
import { Unprotect, UnprotectedValue } from '../ProtectedValue';

import { UnprotectedCustomField } from './CustomField';
import { ElsterCertificate } from './ElsterCertificate';
import { Login, ShareLink, ShareSecret } from './Login';
import { LoginCreditCard } from './LoginCreditCard';
import { UnprotectedPasskey } from './Passkey';

type UnprotectedLogin = Unprotect<Login, UnprotectedValue>;

const UnprotectedShareLink = ShareLink.extend({ secret: UnprotectedValue });
const UnprotectedShareSecret = ShareSecret.extend({ ageRecipient: UnprotectedValue });

const UnprotectedLoginWithoutHistory = objectPassthrough({
  // We can't use extend here, because that doesn't work with objects containing a refinement. We
  // spread the shape of Login instead, dropping the refinement
  ...Login.shape,
  password: UnprotectedValue,
  passkey: UnprotectedPasskey.optional(),
  totp: UnprotectedValue.optional(),
  customFields: z.array(UnprotectedCustomField),
  creditCard: LoginCreditCard.extend({
    pin: UnprotectedValue,
    securityCode: UnprotectedValue,
  }).optional(),
  elsterCertificate: ElsterCertificate.extend({
    certificateData: UnprotectedValue,
  }).optional(),
  shareLinks: z.array(UnprotectedShareLink).optional(),
  shareSecrets: z.array(UnprotectedShareSecret).optional(),
});

const UnprotectedLogin = UnprotectedLoginWithoutHistory.extend({
  // Revisions are left unparsed, mirroring Login.history.revisions. See LoginRevisionHistory.
  history: z
    .object({
      currentRevisionId: z.uuid(),
      revisions: z.record(z.string(), z.unknown()),
    })
    .optional(),
});

const UnprotectedLoginListElement = z.union([
  UnprotectedLogin.safeExtend(ActiveElement.shape),
  UnprotectedLogin.safeExtend(ArchivedElement.shape),
  Tombstone,
]);

// History-less variant for contexts that never carry revision history (e.g. linked
// login update messages, which strip it). Keeps those payloads JSON-serializable —
// history.revisions is otherwise unparsed `unknown`, which is not JSON-compatible.
const UnprotectedLoginWithoutHistoryListElement = z.union([
  UnprotectedLogin.omit({ history: true }).safeExtend(ActiveElement.shape),
  UnprotectedLogin.omit({ history: true }).safeExtend(ArchivedElement.shape),
  Tombstone,
]);

export { UnprotectedLoginListElement, UnprotectedLogin, UnprotectedLoginWithoutHistoryListElement };
